/**
 * Created by Ning Jiang on 3/6/2019.
 */

import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Timeline, TimelineLite, TweenLite} from "gsap";
import {DWBackground} from "../../background/DWBackground";
import {DeadwoodGameConfig} from "../../DeadwoodGameConfig";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {DeadwoodReelSymbol} from "../../symbols/DeadwoodReelSymbol";
import {WinNumberText} from "../individual/DeadwoodIndividualWPView";

class InitialWinGlow extends PIXI.Container {

    static GLOW_ANIMATION_NAME:string = "initialWin_symGlow";

    private _glowSprite:PIXI.Sprite;
    private _glowSpine:PIXI.spine.Spine;

    public get glowSpine():PIXI.spine.Spine {
        return this._glowSpine;
    }

    constructor() {
        super();

        this._glowSpine = new PIXI.spine.Spine(GameResources.getSpineAsset(InitialWinGlow.GLOW_ANIMATION_NAME));
        this._glowSpine.autoUpdate = true;

        this._glowSprite = new PIXI.Sprite();
        this._glowSprite.anchor.set(0.5);
        this._glowSprite.addChild(this._glowSpine);

        this.addChild(this._glowSprite);
    }
}

class InitialWinEffects extends PIXI.Container {

    static PARTICLES_ANIMATION_NAME:string = "InitialWinPt";

    private _particlesSprite:PIXI.Sprite;
    private _particlesSpine:PIXI.spine.Spine;

    public get particlesSpine():PIXI.spine.Spine {
        return this._particlesSpine;
    }

    constructor() {
        super();

        this._particlesSpine = new PIXI.spine.Spine(GameResources.getSpineAsset(InitialWinEffects.PARTICLES_ANIMATION_NAME));
        this._particlesSpine.autoUpdate = true;

        this._particlesSprite = new PIXI.Sprite();
        this._particlesSprite.anchor.set(0.5);
        this._particlesSprite.addChild(this._particlesSpine);

        this.addChild(this._particlesSprite);
    }
}

export class DeadwoodInitialWPView extends BaseView {

    private _layer:ScalableLayer;

    private _winGlowContainer:PIXI.Container;
    private _winGlowPool:InitialWinGlow[] = [];

    private _winEffectsContainer:PIXI.Container;
    private _winEffectsPool:InitialWinEffects[] = [];

    private _wildEffectContainer:PIXI.Container;
    private _wildEffectPool:PIXI.Sprite[] = [];

    constructor() {
        super();
        this._layer = StageManager.getLayer(DeadwoodGameConfig.instance.LAYERS.SYMBOLS_SHOT.name);
    }

    protected initAnimations():void {
        this._winGlowContainer = new PIXI.Container();
        StageManager.getLayer(DeadwoodGameConfig.instance.LAYERS.SYMBOL_WIN_GLOW.name).addChild(this._winGlowContainer);

        this._winEffectsContainer = new PIXI.Container();
        this._layer.addChild(this._winEffectsContainer);

        this._wildEffectContainer = new PIXI.Container();
        this._layer.addChild(this._wildEffectContainer);
    }

    public addSymbolGlowToTimeline(timeline:Timeline, startTime:number, position:[number, number], isWild:boolean):void {
        const overlayTimeline:TimelineLite = new TimelineLite();

        if(isWild) {
            const wildEffect:PIXI.Sprite = this.getWildEffect();
            wildEffect.position.set(position[0], position[1] + GameConfig.instance.SYMBOL_HEIGHT * 0.5);
            this._wildEffectContainer.addChild(wildEffect);

            overlayTimeline.add(TweenLite.to(wildEffect, 0.2, {alpha : 1}));
            overlayTimeline.add(TweenLite.to(wildEffect, 0.3, {alpha : 0}), "+=0.5");
            overlayTimeline.add(() => this.recycleWildEffect(wildEffect));
        } else {
            const winGlow:InitialWinGlow = this.getWinGlow();
            winGlow.position.set(position[0], position[1]);
            this._winGlowContainer.addChild(winGlow);

            const winEffects:InitialWinEffects = this.getWinEffects();
            winEffects.position.set(position[0], position[1]);
            this._winEffectsContainer.addChild(winEffects);

            overlayTimeline.add([
                TweenLite.to(winGlow, 0.2, {alpha : 1}),
                TweenLite.to(winEffects, 0.2, {alpha : 1}),
                new SpineTween(winGlow.glowSpine, 0, InitialWinGlow.GLOW_ANIMATION_NAME),
                new SpineTween(winEffects.particlesSpine, 0, InitialWinEffects.PARTICLES_ANIMATION_NAME)
            ]);
            overlayTimeline.add(() => {
                this.recycleWinGlow(winGlow);
                this.recycleWinEffects(winEffects);
            });
        }

        timeline.add(overlayTimeline, startTime);
    }

    public startWinPresentation():void {
        EventHandler.dispatchEvent(new GameEvent(DWBackground.DIM_REEL, 0.6, 0.2));
    }

    public stopWinPresentation():void {
        this._winEffectsContainer.children.forEach((child:PIXI.DisplayObject) => {
            const winEffects:InitialWinEffects = <InitialWinEffects>child;
            TweenLite.to(winEffects, 0.1, {
                alpha : 0, onComplete : () => this.recycleWinEffects(winEffects)
            });
        });
        this._winGlowContainer.children.forEach((child:PIXI.DisplayObject) => {
            const winGlow:InitialWinGlow = <InitialWinGlow>child;
            TweenLite.to(winGlow, 0.1, {
                alpha : 0, onComplete : () => this.recycleWinGlow(winGlow)
            });
        });
        this._wildEffectContainer.children.forEach((child:PIXI.DisplayObject) => {
            const wildEffect:PIXI.Sprite = <PIXI.Sprite>child;
            TweenLite.to(wildEffect, 0.1, {
                alpha : 0, onComplete : () => this.recycleWildEffect(wildEffect)
            });
        });
        EventHandler.dispatchEvent(new GameEvent(DWBackground.DIM_REEL, 0, 0.1));
    }

    private getWildEffect():PIXI.Sprite {
        const wildEffect:PIXI.Sprite = this._wildEffectPool.length === 0 ? this.createNewWildEffect() : this._wildEffectPool.pop()!;
        wildEffect.alpha = 0;
        return wildEffect;
    }

    private createNewWildEffect():PIXI.Sprite {
        const wildEffect:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("WInitial")[0]);
        wildEffect.blendMode = PIXI.BLEND_MODES.ADD;
        wildEffect.anchor.set(0.5);
        wildEffect.scale.set(2);
        return wildEffect;
    }

    private recycleWildEffect(wildEffect:PIXI.Sprite):void {
        this._wildEffectContainer.removeChild(wildEffect);
        this._wildEffectPool.push(wildEffect);
    }

    private getWinGlow():InitialWinGlow {
        const winGlow:InitialWinGlow = this._winGlowPool.length === 0 ? new InitialWinGlow() : this._winGlowPool.pop()!;
        winGlow.glowSpine.state.clearTrack(0);
        winGlow.glowSpine.skeleton.setToSetupPose();
        winGlow.alpha = 0;

        return winGlow;
    }

    private recycleWinGlow(winGlow:InitialWinGlow):void {
        this._winGlowContainer.removeChild(winGlow);
        this._winGlowPool.push(winGlow);
        winGlow.glowSpine.state.setEmptyAnimation(0, 0);
    }

    private getWinEffects():InitialWinEffects {
        const winEffects:InitialWinEffects = this._winEffectsPool.length === 0 ? new InitialWinEffects() : this._winEffectsPool.pop()!;
        winEffects.particlesSpine.state.clearTrack(0);
        winEffects.particlesSpine.skeleton.setToSetupPose();
        winEffects.alpha = 0;

        return winEffects;
    }

    private recycleWinEffects(winEffects:InitialWinEffects):void {
        this._winEffectsContainer.removeChild(winEffects);
        this._winEffectsPool.push(winEffects);
        winEffects.particlesSpine.state.setEmptyAnimation(0, 0);
    }

    public addWinNumberToTimeline(timeline:Timeline, startTime:number, position:[number, number], symbol:DeadwoodReelSymbol, betWayWinData:IBetWayWinData):void {
        symbol.shouldPlayWinText = {
            position : position,
            betWayWinData : betWayWinData
        };
        timeline.add(() => {
            this.playWinNumberOnPosition(position, symbol, betWayWinData)
        }, startTime);
    }

    public playWinNumberOnPosition(position:[number, number], symbol:DeadwoodReelSymbol, betWayWinData:IBetWayWinData):void {
        const winText = new WinNumberText(betWayWinData.multipliedAmount, 1, betWayWinData.numberOfCombinations);
        winText.position.set(position[0], position[1]);
        const winTextTl = new TimelineLite();
        winTextTl.add(() => {
            symbol.winTextPlayed = true;
            StageManager.getLayer(DeadwoodGameConfig.instance.LAYERS.WIN_TEXT.name).addChild(winText);
        });
        winTextTl.add([
            TweenLite.to(winText, 1, {y : position[1] - 100}),
            TweenLite.to(winText, 0.8, {alpha : 0, delay : 0.2})
        ], `+=0.3`);
        winTextTl.add(() => {
            winText.parent.removeChild(winText);
            winText.destroy();
        });
    }
}